import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, InlineAlertType, InlineAlertWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { MapDispatchToProps, MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { IGetSiteEncryptionDataKeyResult } from '~src/settings/security-settings/sections/credential-encryption/models/CredentialEncryptionModels';
import { IGetSiteEncryptionKeyResult } from '~src/settings/security-settings/sections/extract-encryption/models/ExtractEncryptionModels';
import { IUpdateSiteSettingsForSiteAdminResult, UpdateSiteSettingsForSiteAdminRequest } from '~src/settings/ServerApi/requests/siteSettings/UpdateSiteSettingsForSiteAdminRequest';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { siteSettingsRequestSucceeded } from '../extract-encryption/actions/SiteSettingsRequestAction';
import { updateSiteSettings } from '../extract-encryption/services/UpdateSiteSettingsService';
import { getCredentialEncryptionKeyRequest } from './actions/GetCredentialEncryptionKeyRequestAction';
import { KMSEncryptionKeyTable } from './components/KMSEncryptionKeyTable';
import { openGenerateKMSKeyDialog } from './dialogs/KMSKeyGenerateDialog';
import { openGenerateKMSKeyForbiddenDialog } from './dialogs/KMSKeyGenerateForbiddenDialog';
import { credentialEncryptionSettingsContainer, credentialEncryptionSpaceContainer } from './styles/CredentialEncryptionSettingsStyles';
import { getEnableEkmCredRequest } from './utils/CredentialEncrpytionUtils';


export interface CredentialEncryptionSectionDispatchProps {
  retrieveCredentialEncryptionKey: () => void;
  updateSiteSettings: (siteSettings: ISiteSettingsForSiteAdmin) => void;
}

export interface CredentialEncryptionSectionStateProps {
  readonly isLoading: boolean;
  readonly encryptionDataKey: IGetSiteEncryptionDataKeyResult;
  readonly encryptionKey: IGetSiteEncryptionKeyResult;
  readonly siteSettings: ISiteSettingsForSiteAdmin;
}

interface CredentialEncryptionSectionOwnProps {
  siteId: string;
}

export interface CredentialEncryptionSectionState {
  isChecked: boolean;
  isRotating: boolean;
  isSavingSettings: boolean;
  readonly hasError: boolean;
}

export type CredentialEncryptionSectionProps = CredentialEncryptionSectionOwnProps & CredentialEncryptionSectionDispatchProps & CredentialEncryptionSectionStateProps;

export class CredentialEncryptionSectionComponent extends React.PureComponent<CredentialEncryptionSectionProps, CredentialEncryptionSectionState> {

  public constructor(props: CredentialEncryptionSectionProps) {
    super(props);
    this.state = {
      isChecked: this.isEncryptionEnabled(),
      isRotating: false,
      isSavingSettings: false,
      hasError: false
    };
  }


  public componentDidMount(): void {
    // this.props.retrieveSiteSettings(this.props.siteId);
    this.props.retrieveCredentialEncryptionKey();
  }

  public componentDidUpdate(prevProps: CredentialEncryptionSectionProps, prevState: CredentialEncryptionSectionState): void {
    if(prevProps.encryptionDataKey !== this.props.encryptionDataKey) {
      this.setState({
        isChecked: this.isEncryptionEnabled()
      });
    }
  }

  public render() {
    if(!(this.props?.siteSettings?.siteAdminSettings as ISiteAdminSettings)?.cmekAvailable) {
      return null;
    }

    return (
      <div className={credentialEncryptionSpaceContainer}>
        <div className={credentialEncryptionSettingsContainer}>
          <div className={SettingsStyles.settingsGroupName}>
            {Messages.credentialEncryption_kmsInOnlineTitle()}
          </div>
          <div style={{marginLeft: '25px'}}>
          {this.getEnableCredentialEncryptionSection()}
          {this.getEKMCredSaveSection()}
          {this.getGenerateKeySection()}
          {this.getEncryptionKeyTable()}
        </div>
        </div>
    </div>
    );
  }

  private getEnableCredentialEncryptionSection() {
    return (
    <div>
        <div style={{paddingBottom: '12px'}}>
            {Messages.credentialEncyrptionLearnMore()}
            <span>
              <TextLinkWidget
                  testId={'online-kms-creds-enable-learn-more'}
                  target={'_blank'}
                  href={HelpService.getHelpUrlByCategory('online-kms-creds-enable')}
                  className={learnMoreStyle}>
                  {Messages.learnMore()}
              </TextLinkWidget>
            </span>
        </div>
        <div style={{paddingBottom: '12px'}}>
          <CheckBoxWithLabelWidget
            testId='credential-encrpytion-checkbox'
            label={Messages.credentialEncryptionCheckboxLabel()}
            checked={this.isEncryptionEnabled() || this.state.isChecked }
            handleChange={() => {this.setState({isChecked: !this.state.isChecked});}}
            tabIndex={-1}
            removeLeftMargin={true}
            disabled={ !this.isKMSKeyActive(this.props.encryptionKey) || this.isEncryptionEnabled() }
            />
        </div>
    </div>);
  }

  private getEKMCredSaveSection() {
    return(
      // Only show the save section if the user has checked enable but has not saved yet
      this.state.isChecked && !this.isEncryptionEnabled() ?
      <div>
          <InlineAlertWidget style={{marginBottom: '12px', width: '350px'}} children={Messages.credentialEncrpytionEnableWarning()} testId={'enable-credential-encrpytion-alert'} type={InlineAlertType.Warning} />
          <ButtonWidget
              buttonType={ButtonType.Outline}
              testId='cancel-credential-encryption-settings'
              onClick={() => this.setState({isChecked: !this.state.isChecked})}
              children={Messages.cancel()}
              style={{marginRight: '16px'}}
              />
          <ButtonWidget
              buttonType={ButtonType.Primary}
              testId='save-credential-encryption-settings'
              onClick={() => this.updateSiteSettingsForSiteAdminCredentialEncrpytion()}
              children={Messages.credentialEncrpytionSaveButtonText()}
              disabled={this.state.isSavingSettings}
              showSpinner={this.state.isSavingSettings}
              />
      </div> : null
    );
  }

  private getGenerateKeySection(){
      return (
        this.isEncryptionEnabled()?
            <ButtonWidget
              buttonType={ButtonType.Outline}
              testId='credential-encrpytion-generate-key'
              onClick={() => this.handleClickGenerateDataKey()}
              disabled={this.state.isRotating}
              children={Messages.credentialEncryptionTOLKeyGenerateKeyPrompt()}
              showSpinner={this.state.isRotating}
            /> : null);
  }

  private handleClickGenerateDataKey() {
    const coolDownPeriod = (this.props.siteSettings?.siteAdminSettings as any)?.siteSettingsV2?.dataKeyRotationCoolDownPeriod ?? 7;
    const nextRotationDate = this.getNextRotationDate(coolDownPeriod);
    const currentDate = new Date();
    if (!nextRotationDate || currentDate > nextRotationDate) {
      openGenerateKMSKeyDialog(this.props.encryptionDataKey.dataKeys, coolDownPeriod);
    } else {
      openGenerateKMSKeyForbiddenDialog(nextRotationDate.toISOString(), coolDownPeriod);
    }
  }

  private getNextRotationDate(coolDownPeriod: number): Date | null {
    const dataKeys = this.props.encryptionDataKey?.dataKeys;
    if (!dataKeys || dataKeys.length === 0) {
        return null;  // No keys, no next rotation date
    }

    const firstDataKey = dataKeys[0];
    const createdDate = new Date(firstDataKey.keyCreatedDate);

    // Add cooldown period to creation date
    const nextRotationDate = new Date(createdDate);
    nextRotationDate.setDate(nextRotationDate.getDate() + coolDownPeriod);

    return nextRotationDate;
  }

  private updateSiteSettingsForSiteAdminCredentialEncrpytion() {
    const request: UpdateSiteSettingsForSiteAdminRequest = getEnableEkmCredRequest(this.props.siteSettings);

      this.setState({isSavingSettings: true}, () => updateSiteSettings(request)
      .then((result: IUpdateSiteSettingsForSiteAdminResult) => {
          if (result.errors) {
              ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
          } else {
              ToasterService.instance.pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess() });
              this.props.updateSiteSettings(result as ISiteSettingsForSiteAdmin);
          }
      }).catch(e => {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
      }).finally(() => {
        this.setState({isSavingSettings: false});
      }));
  }

  private getEncryptionKeyTable() {
      if(!this.isEncryptionEnabled()) return null;

      if(this.isKMSKeyGenerated(this.props.encryptionDataKey)) {
        return <KMSEncryptionKeyTable />;
      }

      return null;
  }

  private isEncryptionEnabled(): boolean {
    const hasDataKeys = this.isKMSKeyGenerated(this.props.encryptionDataKey);
    const ekmCredEnabled = (this.props.siteSettings?.siteAdminSettings as any)?.siteSettingsV2?.ekmCredEnabled;
    return ekmCredEnabled || hasDataKeys;
  }

  private isKMSKeyGenerated(key: IGetSiteEncryptionDataKeyResult): boolean {
    return key?.dataKeys?.length > 0;
  }

  private isKMSKeyActive(key: IGetSiteEncryptionKeyResult): boolean {
    return key?.rootKeys?.some(key => key.keyStatus === 'ACTIVE');
  }
}

const mapDispatchToProps: MapDispatchToProps<CredentialEncryptionSectionDispatchProps> =
  (dispatch) => bindActionCreators({
    retrieveCredentialEncryptionKey: getCredentialEncryptionKeyRequest,
    updateSiteSettings: siteSettingsRequestSucceeded
  },
  dispatch);

const mapStateToProps: MapStateToProps<CredentialEncryptionSectionStateProps> = (state: AppState) => ({
  isLoading: state.credentialEncryptionInfo?.isFetchingEncryptionKey,
  encryptionDataKey: state.credentialEncryptionInfo?.encryptionKey,
  encryptionKey: state.extractEncryptionInfo?.encryptionKey,
  siteSettings: state.extractEncryptionInfo.siteSettings
});

export const CredentialEncryptionSection: React.ComponentClass<CredentialEncryptionSectionOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(CredentialEncryptionSectionComponent);